import { z } from 'zod';

import { ZodAmount } from '@Common/types/Amount';
import { ZodB2bPlanCategoryEs, ZodB2bPlanTypeEs } from '@Common/types/b2b/plans';
import {
  withEarlyWithdrawalRequestRefine,
  ZodB2BWithdrawalDocument,
  ZodEarlyWithdrawalRequest,
  ZodWithdrawalAllocation,
  ZodWithdrawalSourceType,
  ZodWithdrawalSummaryAmounts,
  ZodWithdrawalSummaryInstrumentAllocation,
  ZodWithdrawalTaxBreakdown,
} from '@Common/types/b2b/withdraw';
import { ZodB2BEarlyWithdrawalReason } from '@Common/types/funding/withdraw_early_request';
import { ZodInstrumentMutualFundAssetClass } from '@Common/types/instruments/instrument';
import {
  ZodInternalSavingPlanId,
  ZodPositionData,
  ZodTransformedInternalSavingPlanId,
} from '@Common/types/profile/saving_plans';
import { ZodDateLike } from '@Common/types/zod/zod-date-like';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';

export const ZodPlannedWithdrawalFlow = z.enum(['trigger_nav', 'floor_nav', 'floor_price']);
export type PlannedWithdrawalFlow = z.infer<typeof ZodPlannedWithdrawalFlow>;

const ZodPlannedWithdrawalPosition = z.object({
  ...ZodPositionData.shape,
  price: ZodAmount,
});

export type PlannedWithdrawalPosition = z.infer<typeof ZodPlannedWithdrawalPosition>;

const ZodPlannedWithdrawalPlanFunds = z.object({
  plan_id: ZodInternalSavingPlanId,
  type: ZodB2bPlanTypeEs,
  category: ZodB2bPlanCategoryEs,
  withdrawable: ZodAmount,
  total: ZodAmount,
  withdrawable_positions: z.array(ZodPlannedWithdrawalPosition),
  locked_positions: z.array(ZodPlannedWithdrawalPosition),
  restricted_instrument_ids: z.number().int().array(),
});

export type PlannedWithdrawalPlanFunds = z.infer<typeof ZodPlannedWithdrawalPlanFunds>;

const ZodPlannedWithdrawalCategoryFunds = z.object({
  ...ZodPlannedWithdrawalPlanFunds.omit({
    plan_id: true,
    type: true,
    category: true,
    restricted_instrument_ids: true,
  }).shape,
  type: ZodB2bPlanCategoryEs,
});

export type PlannedWithdrawalCategoryFunds = z.infer<typeof ZodPlannedWithdrawalCategoryFunds>;

const ZodCompanyPlannedWithdrawalCategoryFunds = z.object({
  ...ZodPlannedWithdrawalCategoryFunds.shape,
  restricted_instrument_ids: z.number().int().array(),
});

const ZodCompanyPlannedWithdrawalFunds = z.object({
  plans: z.array(ZodPlannedWithdrawalPlanFunds),
  categories: z.array(ZodCompanyPlannedWithdrawalCategoryFunds),
  withdrawable: ZodAmount,
  total: ZodAmount,
});

const ZodPlannedWithdrawalFunds = z.object({
  categories: z.array(ZodPlannedWithdrawalCategoryFunds),
  per_company: z.array(
    z.object({
      ...ZodCompanyPlannedWithdrawalFunds.shape,
      company_id: z.number().int(),
    }),
  ),
  withdrawable: ZodAmount,
  total: ZodAmount,
  flows: z.array(
    z.discriminatedUnion('flow', [
      z.object({
        instrument_id: z.number().int(),
        flow: ZodPlannedWithdrawalFlow.exclude(['floor_price']),
      }),
      z.object({
        instrument_id: z.number().int(),
        underlying_instrument_id: z.number().int(),
        flow: ZodPlannedWithdrawalFlow.extract(['floor_price']),
      }),
    ]),
  ),
});

export type PlannedWithdrawalFunds = z.infer<typeof ZodPlannedWithdrawalFunds>;

const ZodPostFundingEmployeeSavingsPlannedWithdrawalEligibleAllocationsRequestBody = z.object({
  company_id: z.number().int().optional(),
  request: z
    .object({
      products: z.array(ZodB2bPlanCategoryEs),
      date: ZodDateLike,
      reason: ZodB2BEarlyWithdrawalReason,
    })
    .optional(),
});

export const postFundingEmployeeSavingsPlannedWithdrawalEligibleAllocationsEndpoint = endpoint({
  method: 'POST',
  body: ZodPostFundingEmployeeSavingsPlannedWithdrawalEligibleAllocationsRequestBody,
  responseBody: ZodPlannedWithdrawalFunds,
});

export type PostFundingEmployeeSavingsPlannedWithdrawalEligibleAllocationsEndpoint =
  GetEndpointType<typeof postFundingEmployeeSavingsPlannedWithdrawalEligibleAllocationsEndpoint>;

const ZodPostFundingEmployeeSavingsPlannedWithdrawalAllocationBreakdownRequestBody = z.object({
  company_id: z.number().int(),
  instrument_id: z.number().int(),
  product: ZodB2bPlanCategoryEs,
  saving_plan_id: ZodInternalSavingPlanId,
  target_price: z.number().int(),
  request: z
    .object({
      date: ZodDateLike,
      reason: ZodB2BEarlyWithdrawalReason,
    })
    .optional(),
});

const ZodPlannedWithdrawalAllocationDetails = z.object({
  ...ZodWithdrawalAllocation.omit({
    saving_plan_id: true,
    instrument_id: true,
  }).shape,
  saving_plan_id: ZodInternalSavingPlanId,
  instrument_id: z.number().int(),
});

export type PlannedWithdrawalAllocationDetails = z.infer<
  typeof ZodPlannedWithdrawalAllocationDetails
>;

const ZodPostFundingEmployeeSavingsPlannedWithdrawalAllocationBreakdownResponseBody = z.object({
  amount: z.number().int(),
  allocations: z.array(ZodPlannedWithdrawalAllocationDetails),
});

export const postFundingEmployeeSavingsPlannedWithdrawalAllocationBreakdownEndpoint = endpoint({
  method: 'POST',
  pathParams: undefined,
  queryParams: undefined,
  body: ZodPostFundingEmployeeSavingsPlannedWithdrawalAllocationBreakdownRequestBody,
  responseBody: ZodPostFundingEmployeeSavingsPlannedWithdrawalAllocationBreakdownResponseBody,
  headers: undefined,
});

export type PostFundingEmployeeSavingsPlannedWithdrawalAllocationBreakdownEndpoint =
  GetEndpointType<typeof postFundingEmployeeSavingsPlannedWithdrawalAllocationBreakdownEndpoint>;

const ZodPlannedWithdrawalAllocation = ZodPlannedWithdrawalAllocationDetails.omit({
  saving_plan_id: true,
  instrument_id: true,
});

export type PlannedWithdrawalAllocation = z.infer<typeof ZodPlannedWithdrawalAllocation>;

const ZodPostFundingPlannedWithdrawalEmployeeSavingsSummaryRequestBodyObject = z.object({
  company_id: z.number().int(),
  saving_plan_id: ZodTransformedInternalSavingPlanId,
  instrument_id: z.number().int(),
  target_price: z.number().int(),
  allocations: z.array(ZodPlannedWithdrawalAllocation),
  request: ZodEarlyWithdrawalRequest.required().optional(),
});

const ZodPostFundingPlannedWithdrawalEmployeeSavingsSummaryRequestBody =
  withEarlyWithdrawalRequestRefine(
    ZodPostFundingPlannedWithdrawalEmployeeSavingsSummaryRequestBodyObject,
  );

export type B2BPlannedWithdrawalRequestSummaryBody = z.infer<
  typeof ZodPostFundingPlannedWithdrawalEmployeeSavingsSummaryRequestBody
>;

const ZodPlannedWithdrawalSummaryInstrumentAllocation = z.object({
  ...ZodWithdrawalSummaryInstrumentAllocation.omit({
    instrument_asset_class: true,
    instrument_risk: true,
  }).shape,
  instrument_asset_class: ZodInstrumentMutualFundAssetClass.or(z.literal('ccb')),
  instrument_risk: z.number().int().min(0).max(7),
});

export type PlannedWithdrawalSummaryInstrumentAllocation = z.infer<
  typeof ZodPlannedWithdrawalSummaryInstrumentAllocation
>;

const ZodPlannedWithdrawalSummary = z.object({
  ...ZodWithdrawalSummaryAmounts.omit({ allocations: true }).shape,
  type: ZodWithdrawalSourceType,
  requires_approval: z.boolean(),
  tax_breakdown: ZodWithdrawalTaxBreakdown,
  allocations: z.array(ZodPlannedWithdrawalSummaryInstrumentAllocation),
});

const ZodPostFundingPlannedWithdrawalEmployeeSavingsSummaryResponseBody = z.object({
  product: ZodB2bPlanCategoryEs,
  flow: ZodPlannedWithdrawalFlow,
  is_price_guaranteed: z.boolean(),
  expiry_date: z.iso.datetime(),
  summaries: z.array(ZodPlannedWithdrawalSummary),
});

export type PostFundingPlannedWithdrawalEmployeeSavingsSummaryResponseBody = z.infer<
  typeof ZodPostFundingPlannedWithdrawalEmployeeSavingsSummaryResponseBody
>;

export const postFundingPlannedWithdrawalEmployeeSavingsSummaryEndpoint = endpoint({
  method: 'POST',
  pathParams: undefined,
  queryParams: undefined,
  body: ZodPostFundingPlannedWithdrawalEmployeeSavingsSummaryRequestBody,
  responseBody: ZodPostFundingPlannedWithdrawalEmployeeSavingsSummaryResponseBody,
  headers: undefined,
});

export type PostFundingPlannedWithdrawalEmployeeSavingsSummaryEndpoint = GetEndpointType<
  typeof postFundingPlannedWithdrawalEmployeeSavingsSummaryEndpoint
>;

// Zod forbids overwriting keys of refined objects via `.extend()`, so derive from this one
export const ZodPostFundingPlannedWithdrawalEmployeeSavingsRequestBodyObject = z.object({
  ...ZodPostFundingPlannedWithdrawalEmployeeSavingsSummaryRequestBodyObject.shape,
  mdlr_beneficiary_id: z.number().int().nullable(),
  documents: z
    .array(
      z.object({
        ...ZodB2BWithdrawalDocument.shape,
        type: z.literal('early_withdrawal_request'),
      }),
    )
    .max(5)
    .min(1)
    .optional(),
});

const ZodPostFundingPlannedWithdrawalEmployeeSavingsRequestBody = withEarlyWithdrawalRequestRefine(
  ZodPostFundingPlannedWithdrawalEmployeeSavingsRequestBodyObject,
);

export type PostFundingPlannedWithdrawalEmployeeSavingsRequestBody = z.infer<
  typeof ZodPostFundingPlannedWithdrawalEmployeeSavingsRequestBody
>;

export const postFundingPlannedWithdrawalEmployeeSavingsEndpoint = endpoint({
  method: 'POST',
  pathParams: undefined,
  queryParams: undefined,
  body: ZodPostFundingPlannedWithdrawalEmployeeSavingsRequestBody,
  responseBody: z.object({
    withdrawals: z.array(
      z.object({
        id: z.number().int(),
        product: ZodB2bPlanCategoryEs,
      }),
    ),
  }),
  headers: undefined,
});

export type PostFundingPlannedWithdrawalEmployeeSavingsEndpoint = GetEndpointType<
  typeof postFundingPlannedWithdrawalEmployeeSavingsEndpoint
>;
